import type {
  PricingRowLastEdited,
  PricingRowMoneyInput,
} from '../schemas/product-pricing';
import type {
  NormalizedItemInput,
  NormalizedPriceComponent,
  PricingInclusionPolicies,
  PricingInput,
  VatRate,
} from './types';
import {
  calculatePricing,
  excludeVatFromAmount,
  roundMoneyAmount,
  roundPercentValue,
} from './engine';

// Product-edit Pricing-tab row math. This is a thin ADAPTER, not a formula:
// it maps a single editor row + the last-edited field onto a one-item
// PricingInput, runs the shared `calculatePricing` engine (the single source
// of pricing math — currency.js precision, VAT, markup/commission), and reads
// the computed money fields back. Both the BE `productPricing.computeRow` and
// the FE live recalc call this, so there is exactly one calculation path.

export type ComputePricingRowArgs = {
  pricingType: 'net' | 'gross';
  costVatPercent: number | null;
  priceVatPercent: number | null;
  lastEdited: PricingRowLastEdited;
  row: PricingRowMoneyInput;
};

export type ComputedPricingRow = {
  cost: number | null;
  netCost: number | null;
  markup: number | null;
  price: number | null;
  priceIncl: number | null;
  commission: number | null;
};

const ITEM_ID = 'product-pricing-row';

// Single-item compute always includes the one row regardless of flags.
const policyRules = <S extends keyof PricingInclusionPolicies>(
  scope: S
): PricingInclusionPolicies[S] =>
  ({
    scope,
    hiddenItems: 'include',
    optionalItems: 'include',
    lineItems: 'include',
    manualLineItems: 'include',
    parentItems: 'include',
    childItems: 'include',
    subitems: 'include',
    deletedItems: 'exclude',
    excludedItems: 'exclude',
    parentChildMode: 'includeParentAndChildren',
  }) as PricingInclusionPolicies[S];

const buildInput = (item: NormalizedItemInput): PricingInput => ({
  context: {
    organizationId: 'product-edit',
    itineraryId: null,
    itineraryVersionId: null,
    baseCurrency: 'EUR',
    source: 'frontend-edit-state',
  },
  travellers: [],
  travellerQuantities: [],
  items: [item],
  exchangeRates: {
    baseCurrency: 'EUR',
    rates: [],
    allowFallbackOne: true,
    preserveLegacyInverseResetBehavior: true,
  },
  inclusionPolicies: {
    pricingTableDisplay: policyRules('pricingTableDisplay'),
    pricingTableTotal: policyRules('pricingTableTotal'),
    versionPrice: policyRules('versionPrice'),
    budget: policyRules('budget'),
    export: policyRules('export'),
  },
  editIntents: [],
  moneyPrecision: {
    mode: 'currency-js-compatible',
    decimalPlaces: 2,
    roundingMode: 'currency-js',
    preserveCurrencyJsSemantics: true,
  },
});

export const computePricingRow = ({
  pricingType,
  costVatPercent,
  priceVatPercent,
  lastEdited,
  row,
}: ComputePricingRowArgs): ComputedPricingRow => {
  const isGross = pricingType === 'gross';
  const costVat: VatRate = {
    id: null,
    percent: costVatPercent,
    source: 'cost',
  };
  const priceVat: VatRate = {
    id: null,
    percent: priceVatPercent,
    source: 'price',
  };

  // Choose which field drives the price. The engine resolves the unit price by
  // priority (unitPrice > markupPercent), so we set exactly one source field to
  // express last-edited-wins. This mirrors the legacy PriceCalculator's
  // `userEditedPrice` flag (travel_planner_front
  // form/ProductForm/components/PriceCalculator): only editing the Price field
  // makes price the driver and re-derives markup; editing markup OR any cost
  // field (cost / VAT / commission / pricingType) holds MARKUP and re-derives
  // the price as cost × (1 + markup/100).
  let markupPercent: number | null = null;
  let unitPrice: number | null = null;
  switch (lastEdited) {
    case 'price':
      unitPrice = row.price ?? null;
      break;
    case 'priceIncl':
      unitPrice =
        row.priceIncl != null
          ? excludeVatFromAmount(row.priceIncl, priceVat)
          : null;
      break;
    default:
      // markup / cost / netCost / costVat / priceVat / pricingType / commission.
      // Markup is the driver. Legacy coerces a missing markup to 0 (so editing
      // a cost on a row with no markup yields markup 0 / price = cost), instead
      // of holding the stale saved price — which would otherwise back-compute a
      // wild markup like (760 − 100) / 100 = 660%.
      markupPercent = row.markup ?? 0;
      break;
  }

  const component: NormalizedPriceComponent = {
    currency: 'EUR',
    cost: row.cost ?? null,
    netCost: null, // always derived from cost − VAT (and commission in gross)
    unitPrice,
    finalPrice: null,
    finalPriceWithTax: null,
    profit: null,
    markupPercent,
    commissionPercent: isGross ? (row.commission ?? null) : null,
    units: 1,
    costVat,
    priceVat,
    isGross,
    useNetCost: false,
  };

  const output = calculatePricing(
    buildInput({
      id: ITEM_ID,
      parentId: null,
      childIds: [],
      title: 'Product',
      contentCategory: null,
      contentSubcategory: null,
      dateRange: { startDate: null, endDate: null, nights: 1 },
      flags: {
        isHidden: false,
        isOptional: false,
        isLine: false,
        isManualLine: false,
        isParent: false,
        isChild: false,
        isSubitem: false,
        isDeleted: false,
        isExcluded: false,
      },
      priceStructure: { kind: 'single', unit: 'UNIT', price: component },
    })
  );

  const money = output.pricingTable.rows[0]?.money;
  if (!money) {
    // Engine rejected the input (shouldn't happen) — echo raw cost only.
    return {
      cost: row.cost ?? null,
      netCost: null,
      markup: null,
      price: null,
      priceIncl: null,
      commission: null,
    };
  }

  // The row values feed form state that is displayed and eventually saved
  // into price JSONB: money must be proper 2dp amounts (a markup-driven
  // recompute yields raw 4dp arithmetic like 490.0098), percents keep the
  // engine's 8dp so a later markup-driven recompute reproduces the price.
  const moneyOrNull = (value: number | null): number | null =>
    value === null ? null : roundMoneyAmount(value);
  const percentOrNull = (value: number | null): number | null =>
    value === null ? null : roundPercentValue(value);

  // A zero cost implies no markup from any price, so the engine reports 0 —
  // which would wipe the typed markup as soon as a field blurs. Keep it so the
  // margin applies once the cost is filled in.
  const typedMarkup = markupPercent ?? row.markup ?? null;
  const keepTypedMarkup = typedMarkup !== null && money.cost === 0;

  return {
    cost: moneyOrNull(money.cost),
    netCost: moneyOrNull(money.netCost),
    markup: keepTypedMarkup
      ? percentOrNull(typedMarkup)
      : percentOrNull(money.markupPercent),
    price: moneyOrNull(money.unitPrice),
    priceIncl: moneyOrNull(money.finalPriceWithTax),
    commission: isGross ? percentOrNull(money.commissionPercent) : 0,
  };
};
